import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Send, Star, Eye, Hand, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeInUp}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Header Component
const Header = () => {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0a0a1a]/90 border-b border-purple-900/30">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="/astrodots" className="flex items-center gap-3" data-testid="astrodots-logo">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-10 w-10 object-contain" />
            <span className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </a>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#features" className="text-sm font-medium text-gray-300 hover:text-purple-400 transition-colors">Features</a>
            <a href="#gallery" className="text-sm font-medium text-gray-300 hover:text-purple-400 transition-colors">Gallery</a>
            <a href="#support" className="text-sm font-medium text-gray-300 hover:text-purple-400 transition-colors">Support</a>
          </nav>
          <Button 
            asChild
            variant="ghost"
            className="text-gray-300 hover:text-white hover:bg-purple-900/30"
            data-testid="back-home-btn"
          >
            <a href="/">
              <ArrowLeft className="w-4 h-4 mr-2" />
              LabrHood Labs
            </a>
          </Button>
        </div>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="min-h-screen flex items-center pt-20 bg-gradient-to-b from-[#0a0a1a] via-[#1a1a3a] to-[#0a0a1a] relative overflow-hidden" data-testid="astrodots-hero">
      {/* Background stars effect */}
      <div className="absolute inset-0 opacity-50">
        <div className="absolute top-20 left-20 w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
        <div className="absolute top-40 right-40 w-1 h-1 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }} />
        <div className="absolute top-60 left-1/3 w-1.5 h-1.5 bg-purple-300 rounded-full animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute bottom-40 right-1/4 w-2 h-2 bg-indigo-400 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }} />
        <div className="absolute bottom-60 left-1/4 w-1 h-1 bg-purple-500 rounded-full animate-pulse" style={{ animationDelay: '0.7s' }} />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 sm:px-12 py-24 sm:py-32 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimatedSection>
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-purple-400" />
                <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400">
                  Celestial Intelligence
                </span>
              </div>
              <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-white leading-[0.95]" data-testid="astrodots-headline">
                Discover Your Cosmic Blueprint
              </h1>
              <p className="text-lg sm:text-xl text-gray-400 leading-relaxed font-light max-w-lg">
                Advanced celestial resonance and bio-rhythm analysis platform using computer vision for iris and palm metrics. Unlock the mysteries written in your eyes and hands.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  asChild
                  className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-base font-medium"
                  data-testid="download-btn"
                >
                  <a href="#support">Get Started</a>
                </Button>
                <Button 
                  asChild
                  variant="outline"
                  className="border-purple-500 text-purple-400 hover:bg-purple-900/30 px-8 py-6 text-base font-medium"
                >
                  <a href="#gallery">View Gallery</a>
                </Button>
              </div>
            </div>
          </AnimatedSection>
          
          <AnimatedSection className="relative">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.3 }}
            >
              <img
                src="/astrodots.png"
                alt="AstroDots cosmic visualization"
                className="w-full aspect-square object-cover rounded-full border-4 border-purple-500/30 shadow-2xl shadow-purple-500/20"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 rounded-full bg-gradient-to-t from-purple-900/50 to-transparent" />
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    {
      icon: Eye,
      title: "Iris Odyssey",
      description: "Advanced iris pattern recognition to analyze your celestial resonance and bio-rhythmic signatures."
    },
    {
      icon: Hand,
      title: "Celestial Palmistry",
      description: "AI-powered palm line analysis combined with astrological insights for personalized readings."
    },
    {
      icon: Star,
      title: "Cosmic Alignment",
      description: "Real-time celestial body tracking to provide context-aware astrological interpretations."
    }
  ];

  return (
    <section id="features" className="py-24 sm:py-32 bg-[#0a0a1a]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-4 block">Features</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Unlock Your Celestial Potential
          </h2>
        </AnimatedSection>
        
        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <AnimatedSection key={index}>
              <div className="p-8 bg-gradient-to-b from-purple-900/20 to-transparent border border-purple-500/20 hover:border-purple-500/40 transition-colors" data-testid={`feature-${index}`}>
                <feature.icon className="w-12 h-12 text-purple-400 mb-6" />
                <h3 className="font-heading text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed font-light">{feature.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
};

// Gallery Section
const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const images = [
    { src: "/astrodots-iris.png", title: "Iris Odyssey", description: "Celestial patterns in your eyes" },
    { src: "/astrodots-palm.png", title: "Celestial Palmistry", description: "Cosmic lines on your palm" },
    { src: "/astrodots.png", title: "Cosmic Blueprint", description: "Your universal connection" }
  ];

  return (
    <section id="gallery" className="py-24 sm:py-32 bg-gradient-to-b from-[#0a0a1a] to-[#1a1a3a]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <AnimatedSection className="text-center mb-16">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-4 block">AstroLabs</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white">
            Visual Gallery
          </h2>
        </AnimatedSection>
        
        <motion.div
          ref={ref}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          variants={staggerContainer}
          className="grid md:grid-cols-3 gap-8"
        >
          {images.map((image, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="group relative overflow-hidden"
              data-testid={`gallery-item-${index}`}
            >
              <motion.img
                src={image.src}
                alt={image.title}
                className="w-full aspect-square object-cover"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.4 }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900/90 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-6">
                <div>
                  <h3 className="font-heading text-xl font-bold text-white">{image.title}</h3>
                  <p className="text-purple-300 text-sm">{image.description}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Support Section
const SupportSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/astrodots/support`, formData);
      toast.success("Support request submitted!", {
        description: "We'll get back to you within 24-48 hours."
      });
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error submitting support request:", error);
      toast.error("Failed to submit request", {
        description: "Please try again later."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="support" className="py-24 sm:py-32 bg-[#0a0a1a]" data-testid="support-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid lg:grid-cols-2 gap-16">
          <AnimatedSection>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-purple-400 mb-4 block">Support</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-white mb-6" data-testid="support-headline">
              Need Help?
            </h2>
            <p className="text-gray-400 text-lg font-light mb-8">
              Our cosmic support team is here to assist you with any questions about AstroDots.
            </p>
            <div className="space-y-4 text-gray-400">
              <p>• Technical support and troubleshooting</p>
              <p>• Feature requests and feedback</p>
              <p>• Account and subscription inquiries</p>
              <p>• General questions about the app</p>
            </div>
            <div className="mt-8 pt-8 border-t border-purple-900/30">
              <p className="text-sm text-gray-500">
                By using AstroDots, you agree to our{" "}
                <a href="/astrodots/privacy" className="text-purple-400 hover:text-purple-300">Privacy Policy</a>
                {" "}and{" "}
                <a href="/astrodots/terms" className="text-purple-400 hover:text-purple-300">Terms of Service</a>.
              </p>
            </div>
          </AnimatedSection>
          
          <AnimatedSection>
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="support-form">
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Your Name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-transparent border-0 border-b border-purple-500/30 rounded-none text-white placeholder:text-gray-500 px-0 py-3 focus:ring-0 focus:border-purple-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-transparent border-0 border-b border-purple-500/30 rounded-none text-white placeholder:text-gray-500 px-0 py-3 focus:ring-0 focus:border-purple-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="support-email-input"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="subject"
                  placeholder="Subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-transparent border-0 border-b border-purple-500/30 rounded-none text-white placeholder:text-gray-500 px-0 py-3 focus:ring-0 focus:border-purple-400 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="support-subject-input"
                />
              </div>
              <div>
                <Textarea
                  name="message"
                  placeholder="Describe your issue or question..."
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="bg-transparent border-0 border-b border-purple-500/30 rounded-none text-white placeholder:text-gray-500 px-0 py-3 focus:ring-0 focus:border-purple-400 resize-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="support-message-input"
                />
              </div>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="bg-purple-600 hover:bg-purple-700 text-white w-full sm:w-auto px-8 py-6 text-base font-medium inline-flex items-center justify-center gap-2"
                data-testid="support-submit-btn"
              >
                {isSubmitting ? "Sending..." : (
                  <>
                    Send Message
                    <Send className="w-4 h-4" />
                  </>
                )}
              </Button>
            </form>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="py-12 bg-[#0a0a1a] border-t border-purple-900/30" data-testid="footer-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="/astrodots-logo.png" alt="AstroDots" className="h-8 w-8 object-contain" />
            <span className="font-heading text-lg font-bold text-white">
              Astro<span className="text-purple-400">Dots</span>
            </span>
          </div>
          <div className="flex items-center gap-6 text-sm text-gray-500">
            <a href="/astrodots/privacy" className="hover:text-purple-400 transition-colors">Privacy Policy</a>
            <a href="/astrodots/terms" className="hover:text-purple-400 transition-colors">Terms of Service</a>
            <a href="#support" className="hover:text-purple-400 transition-colors">Support</a>
          </div>
          <p className="text-sm text-gray-500">
            &copy; 2026 LabrHood Labs
          </p>
        </div>
      </div>
    </footer>
  );
};

export default function AstroDots() {
  return (
    <div className="min-h-screen bg-[#0a0a1a]">
      <Header />
      <main>
        <HeroSection />
        <FeaturesSection />
        <GallerySection />
        <SupportSection />
      </main>
      <Footer />
    </div>
  );
}
